import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { apiClient, API_ENDPOINTS, API_BASE_URL } from '../config/api';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { toUtcWallClock, formatEventForViewer, formatInstantForViewer, eventLocalDateKey, getViewerTimezone, resolveTimezoneFromState, nowWallClockInViewerZone, instantToWallClockInZone } from '../utils/dateTime';
import { notesService } from '../services/notes.service';
import InviteeInput from '../components/calendar/InviteeInput';
import { EMAIL_INVITES_ENABLED, parseInvitees, canEmailInvites, shouldSendEmailInvites, newInvitees } from '../utils/calendarInvites';
import './AgentCalendarPage.css';

// Broker-shaped roles (same list as TransactionsPage.js / CreateServiceRequestPage.js).
const CALENDAR_BROKER_ROLES = ['BROKER', 'BROKER_MANAGER', 'BROKER_ADMIN'];
// Broker/Brokerage transaction statuses for the Add Event dropdown — matches the
// Transactions page's Active tab (brokers/brokerages never see Prospect/PENDING).
const BROKER_TXN_STATUSES = ['ACTIVE', 'UNDER_CONTRACT', 'CLOSING'];

// ── Transaction dropdown (same design as InviteSomeonePage) ──────────────────
const TransactionDropdown = ({ transactions, value, onChange, placeholder, isLoading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = transactions.find(t => String(t.id) === String(value)) || null;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (id) => { onChange(id); setOpen(false); };

  return (
    <div className="isv-txn-dd" ref={ref}>
      <button
        type="button"
        className={`isv-txn-trigger${open ? ' isv-txn-trigger--open' : ''}`}
        onClick={() => !isLoading && setOpen(o => !o)}
        disabled={isLoading}
      >
        <span className="isv-txn-trigger-content">
          {isLoading ? (
            <span className="isv-txn-placeholder">Loading transactions...</span>
          ) : selected ? (
            <>
              {selected.transactionNumber && (
                <span className="isv-txn-sel-num">{selected.transactionNumber}</span>
              )}
              <span className="isv-txn-sel-name">
                {selected.transactionName || selected.name || selected.propertyAddress || selected.dealName || '—'}
              </span>
              {selected.propertyAddress && (
                <span className="isv-txn-sel-addr">{selected.propertyAddress}</span>
              )}
            </>
          ) : (
            <span className="isv-txn-placeholder">{placeholder}</span>
          )}
        </span>
        <svg
          className={`isv-txn-chevron${open ? ' isv-txn-chevron--up' : ''}`}
          width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="isv-txn-menu">
          <div
            className={`isv-txn-option${!value ? ' isv-txn-option--active' : ''}`}
            onClick={() => handleSelect('')}
          >
            <span className="isv-txn-opt-none">{placeholder}</span>
          </div>
          {transactions.length === 0 ? (
            <div className="isv-txn-option isv-txn-opt-empty">No transactions found</div>
          ) : (
            transactions.map(txn => (
              <div
                key={txn.id}
                className={`isv-txn-option${String(txn.id) === String(value) ? ' isv-txn-option--active' : ''}`}
                onClick={() => handleSelect(String(txn.id))}
              >
                {txn.transactionNumber && (
                  <span className="isv-txn-opt-num">{txn.transactionNumber}</span>
                )}
                <span className="isv-txn-opt-name">
                  {txn.transactionName || txn.name || txn.propertyAddress || txn.dealName || '—'}
                </span>
                {txn.propertyAddress && (
                  <span className="isv-txn-opt-addr">{txn.propertyAddress}</span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ── Constants ─────────────────────────────────────────────────────────────────
// Canonical Event-type catalog, grouped by the five legend categories. The
// select renders each group as its own <optgroup>; mini-calendar dot colors
// follow the group color (EVENT_COLORS below is derived from this).
const EVENT_TYPE_GROUPS = [
  { label: 'Meetings',        items: ['Client Meeting', 'Listing Presentation', 'Buyer Consultation', 'Call', 'Internal / Team Meeting'] },
  { label: 'Property events', items: ['Open House', 'Broker’s Open / Caravan', 'Showing / Property Tour', 'Final Walkthrough'] },
  { label: 'Services',        items: ['Home Inspection', 'Appraisal', 'Pest / WDO Inspection', 'Environmental Testing', 'Land Survey', 'Photography', 'Staging', 'Cleaning', 'Repairs / Contractor', 'Notary / Loan Signing'] },
  { label: 'Closing',         items: ['Closing / Signing', 'Escrow Appointment'] },
  { label: 'Personal',        items: ['Reminder', 'Blocked Time', 'Other'] },
];
const EVENT_TYPES_FLAT = EVENT_TYPE_GROUPS.flatMap((g) => g.items);
const DEFAULT_EVENT_TYPE = EVENT_TYPES_FLAT[0];

// Reverse lookup: Event type → group label. Used for every rule gated on the
// event's category (Services ⇒ Raise-service-request link, Reminder/Blocked
// Time ⇒ hide Guests/Location/End/All day, etc.).
const EVENT_TYPE_TO_GROUP = EVENT_TYPE_GROUPS.reduce((acc, g) => {
  g.items.forEach((item) => { acc[item] = g.label; });
  return acc;
}, {});
const isReminderType     = (t) => t === 'Reminder';
const isBlockedTimeType  = (t) => t === 'Blocked Time';
const isServicesType     = (t) => EVENT_TYPE_TO_GROUP[t] === 'Services';
// "Guests" field visible for Meetings · Property events · Closing · Services ·
// Other. Hidden for Reminder and Blocked Time (per the Add Event spec).
const showGuestsForType  = (t) => !isReminderType(t) && !isBlockedTimeType(t);
// "Location" field hidden for Reminder and Blocked Time; shown everywhere else.
const showLocationForType = (t) => !isReminderType(t) && !isBlockedTimeType(t);

// ── Start/End time helpers (End = Start + 1 h default) ────────────────────────
const addMinutesToTime = (time, minutes) => {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const total = Math.min(h * 60 + m + minutes, 23 * 60 + 59);
  const hh = Math.floor(total / 60).toString().padStart(2, '0');
  const mm = (total % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
};
const minutesBetweenTimes = (start, end) => {
  if (!start || !end) return 60;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? diff : 60;
};

const WEEKDAYS    = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Each legend group has one color shared by every event type inside it, so
// the mini-calendar dots roll up to the groups the user actually sees in the
// legend chips.
const GROUP_COLORS = {
  'Meetings':        '#5B47D6',
  'Property events': '#10b981',
  'Services':        '#3b82f6',
  'Closing':         '#ef4444',
  'Personal':        '#9ca3af',
};
const EVENT_COLORS = EVENT_TYPE_GROUPS.reduce((acc, g) => {
  const color = GROUP_COLORS[g.label];
  g.items.forEach((item) => { acc[item] = color; });
  return acc;
}, {});

// Note reminders (from My Notes) — separate dot color, not part of EVENT_COLORS
// since notes aren't calendar events and don't have an eventType. Grey ties
// them to the Personal legend group.
const NOTE_REMINDER_COLOR = '#9ca3af';
const LEGEND = [
  { label: 'Meetings',        color: GROUP_COLORS['Meetings'] },
  { label: 'Property events', color: GROUP_COLORS['Property events'] },
  { label: 'Services',        color: GROUP_COLORS['Services'] },
  { label: 'Closing',         color: GROUP_COLORS['Closing'] },
  { label: 'Personal',        color: GROUP_COLORS['Personal'] },
  { label: 'Milestone',       color: '#f59e0b' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const buildGrid = (year, month) => {
  const firstDay     = new Date(year, month, 1).getDay();
  const daysInMonth  = new Date(year, month + 1, 0).getDate();
  const prevMonthEnd = new Date(year, month, 0).getDate();
  const cells        = [];
  for (let i = firstDay - 1; i >= 0; i--) cells.push({ d: prevMonthEnd - i, other: true });
  for (let d = 1; d <= daysInMonth; d++)   cells.push({ d, other: false });
  const rem = (7 - (cells.length % 7)) % 7;
  for (let i = 1; i <= rem; i++)           cells.push({ d: i, other: true });
  return cells;
};

const monthLabel = (year, month) =>
  new Date(year, month, 1).toLocaleString('default', { month: 'long', year: 'numeric' });

const dayLabel = (date) =>
  date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

const getFirebaseToken = () =>
  new Promise((resolve, reject) => {
    const stored = localStorage.getItem('firebaseToken');
    if (stored) { resolve(stored); return; }
    let unsub;
    const t = setTimeout(() => { if (unsub) unsub(); reject(new Error('timeout')); }, 5000);
    unsub = onAuthStateChanged(auth, async (user) => {
      clearTimeout(t);
      unsub();
      if (user) {
        try { resolve(await user.getIdToken(true)); } catch (e) { reject(e); }
      } else {
        reject(new Error('no user'));
      }
    });
  });

// defaultEventType is the initial value for the Event Type <select> —
// intentionally NOT empty. React's controlled <select value=""> with no
// matching option visually snaps to the first <option> but keeps state at
// "", which then hits the backend's @NotBlank eventType validation and
// returns 400. Callers pass the current dropdown's first option (typically
// the fetched catalog's first label, else the fallback constant) so the
// visible-vs-state values match from the very first render.
const emptyForm = (date, time = '', defaultEventType = DEFAULT_EVENT_TYPE) => ({
  title:         '',
  // `titleTouched` turns on the first time the user types into Title; from
  // that point on the Event-type / Transaction auto-fill stops rewriting
  // whatever they chose. Not persisted — only exists on the in-flight form.
  titleTouched:  false,
  // `locationTouched` is the Location-field twin of titleTouched: when a
  // transaction is tagged, Location is seeded with the transaction's
  // propertyAddress so the agent doesn't have to retype what the SR flow
  // already knows. The seed stops the moment the user types in Location.
  locationTouched: false,
  scheduledDate: fmtDate(date),
  scheduledTime: time,
  endTime:       addMinutesToTime(time, 60),
  allDay:        false,
  eventType:     defaultEventType,
  description:   '',
  location:      '',
  transactionId: '',
  invitees:      '',
  // E-mailed calendar invite (.ics) to the guests above. Only offered when
  // EMAIL_INVITES_ENABLED and the event isn't linked to a transaction.
  sendEmailInvites: true,
  // Guest emails already on the event when it was opened for edit — the update
  // endpoint doesn't take guests, so only new ones are added via /invite.
  existingInvitees: [],
});

// ── SVG icons ─────────────────────────────────────────────────────────────────
const CalendarIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const ChevLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const ChevRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

// Transform the dedicated /api/v1/transactions/calendar-timeline feed into Timeline
// cards. Unlike the Dashboard next-action feed, this is UNCAPPED — one card per dated
// step (prospect stage / active milestone / listing stage), past/present/future — so
// no pending step is ever hidden. Events are NOT here (they come from calendar_events),
// so the two sources never duplicate. Each item already carries its own date.
// `todayStr` is the viewer's OWN profile-state timezone day ("yyyy-MM-dd"), passed in
// by the caller (this is a module-level fn with no access to the component's viewer
// zone). Falls back to the device day only if a caller omits it. This keeps the
// Overdue/Due today/Upcoming labels on the viewer's local calendar day, matching the
// rest of this page (grid, events) which already use the viewer zone.
const transformTimelineItems = (items, todayStr = fmtDate(new Date())) => {
  return (items || [])
    .filter(it => it.date)
    .map(it => ({
      // Composite key: an item is unique per (transaction, kind, itemId).
      id:              `${it.transactionId}-${it.kind}-${it.itemId || it.name}`,
      transactionName: it.type ? `${it.transactionName} - ${it.type}` : it.transactionName,
      type:            it.type,
      phase:           it.phase,
      nextActionName:  it.name,
      status:          it.status,
      dueLabel:        it.date < todayStr ? 'Overdue' : (it.date === todayStr ? 'Due today' : 'Upcoming'),
      dueDate:         it.date,
    }))
    .sort((a, b) => (a.dueDate || todayStr).localeCompare(b.dueDate || todayStr));
};

// ── Main component ────────────────────────────────────────────────────────────
const AgentCalendarPage = () => {
  const navigate = useNavigate();

  // Service Providers get their own Calendar page/URL (/calendar/sp) — redirect
  // them away from here so bookmarks/links to the old shared /calendar still land
  // on the right page (mirrors DashboardPage's /dashboard → /dashboard/sp redirect).
  useEffect(() => {
    const role = getActiveRoleFromStorage();
    if (role && role.toUpperCase() === 'SERVICE_PROVIDER') {
      navigate('/calendar/sp', { replace: true });
    }
  }, [navigate]);

  // "Now" in the viewer's own profile timezone (not the device's) — the
  // calendar's initial view, its "today" highlight, and the "Add Event"
  // form's default date/time all key off this so they agree with each
  // other and with create-service-request's identical pre-fill, even when
  // the device's local day disagrees with the viewer's zone (e.g. a device
  // set to a zone far ahead/behind the profile's zone near a day boundary —
  // the device can be on "tomorrow" while the viewer's own zone is still on
  // "today"). Computed once on mount via a lazy initializer so every
  // fresh-form open this session reuses the same "now" reading.
  const [nowInViewerZone] = useState(() => nowWallClockInViewerZone());
  const todayDate = (() => {
    const [y, m, d] = nowInViewerZone.localDate.split('-').map(Number);
    return new Date(y, m - 1, d);
  })();

  const [curYear,   setCurYear]   = useState(todayDate.getFullYear());
  const [curMonth,  setCurMonth]  = useState(todayDate.getMonth());
  const [selDate,   setSelDate]   = useState(todayDate);
  const [allEvents,     setAllEvents]     = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [dotMap,        setDotMap]        = useState({});
  // Timeline cards — from /api/v1/transactions/calendar-timeline (see
  // transformTimelineItems above), separate fetch/state from allEvents above. Returns
  // EVERY dated step across the user's deals (uncapped), so it fetches once rather than
  // refetching on month navigation.
  const [timelineCards,   setTimelineCards]   = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineDotMap,  setTimelineDotMap]  = useState({});
  // Note reminders — from My Notes (GET /api/v1/users/me/notes?hasReminder=true),
  // separate fetch/state from allEvents/timelineCards above. Read-only display only:
  // no add/edit/delete affordance here — notes are only ever created/edited on the
  // My Notes page itself. reminderDate is a plain LocalDate serialized as
  // "yyyy-MM-dd" (see NoteResponse.java), matching fmtDate()'s output exactly, so
  // day-key comparisons below need no timezone conversion.
  const [reminderNotes,  setReminderNotes]  = useState([]);
  const [notesLoading,   setNotesLoading]   = useState(false);
  const [notesDotMap,    setNotesDotMap]    = useState({});
  const [showModal, setShowModal] = useState(false);
  const [transactions,    setTransactions]    = useState([]);
  const [txnLoading,      setTxnLoading]      = useState(false);
  // Canonical Event-Type catalog from GET /api/v1/service-types — same source
  // Create Service Request uses, so the calendar can display any label the
  // catalog produces (e.g. "Cleaning (optional)") instead of clipping to a
  // hardcoded six-item list. Seeded with the fallback constant so the picker
  // renders instantly; live fetch overwrites on mount.
  // Event-type picker uses the hardcoded EVENT_TYPE_GROUPS catalog so Meetings
  // / Property events / Services / Closing / Personal section headers can be
  // rendered consistently (no round-trip to /api/v1/service-types). The flat
  // list is kept for the off-catalog "prepend current value" check in the JSX.
  const eventTypeOptions = EVENT_TYPES_FLAT;
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState('');
  const [form,      setForm]      = useState(emptyForm(todayDate, nowInViewerZone.localTime));
  const [editEventId,    setEditEventId]    = useState(null);
  const [viewOnly,       setViewOnly]       = useState(false); // eye button on a card → same dialog, read-only
  // SR-backed events show a Date-only picker in the dialog; the SR record owns
  // scheduledTime, so surfacing it here (even read-only) would suggest it can
  // be changed from the calendar side.
  const [viewingSrEvent, setViewingSrEvent] = useState(false);
  const [editLoading,    setEditLoading]    = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleting,       setDeleting]       = useState(false);
  const [deleteError,    setDeleteError]    = useState('');

  // Viewer's own timezone — starts from the generic localStorage-based
  // fallback, then upgrades to the SP profile's State (GET /api/v1/users/me/
  // sp-profile) when available, same source and pattern already used on
  // JobsPage.js/JobDetailPage.js, so a service-provider viewer sees the same
  // timezone here as on their own Jobs pages. Silently keeps the fallback
  // for any viewer without an SP profile — expected, not an error.
  const [viewerTimezone, setViewerTimezone] = useState(() => getViewerTimezone());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        const resp = await apiClient.get(API_ENDPOINTS.spProfile, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && typeof resp.data?.state === 'string' && resp.data.state.trim()) {
          setViewerTimezone(resolveTimezoneFromState(resp.data.state));
        }
      } catch {
        // Non-SP viewer or transient failure — keep the localStorage-based fallback.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const todayKey  = fmtDate(todayDate);
  const selKey    = fmtDate(selDate);
  const grid      = buildGrid(curYear, curMonth);

  // Events visible in the day panel — filtered client-side from the full set.
  // eventLocalDateKey re-derives the viewer-local day for UTC-encoded events
  // (see dateTime.js) instead of comparing the raw stored digits, since those
  // can land on a different calendar day than the viewer's own.
  // Sorted by local wall-clock start time so the day panel reads top-down from
  // earliest to latest. Events with no scheduledTime (SR-backed records whose
  // time comes from the SR, or legacy entries) sink to the end rather than
  // sorting first on an empty string.
  const events = allEvents
    .filter(e => eventLocalDateKey(e, viewerTimezone) === selKey)
    .slice()
    .sort((a, b) => {
      const ta = a.scheduledTime || '';
      const tb = b.scheduledTime || '';
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return ta.localeCompare(tb);
    });
  // Timeline cards visible in the day panel — same day-match pattern, matched
  // against the user-selected calendar date (selKey) by dueDate.
  const dayTimelineCards = timelineCards.filter(c => c.dueDate === selKey);
  // Note reminders visible in the day panel — same day-match pattern, matched
  // by reminderDate (already "yyyy-MM-dd", see fetchReminderNotes above).
  const dayReminderNotes = reminderNotes.filter(n => n.reminderDate === selKey);

  // Service Requests for whichever transactions the *currently visible day's*
  // service-type events belong to — lets those events show the SR's
  // requestedDate/requestedTime (always a real, convertible UTC instant)
  // instead of the calendar event's own scheduledDate/scheduledTime, which
  // can lack a timeZone marker. Same idea as TransactionTimeline.js's srById,
  // but fetched per-transaction on demand (scoped to one day's events, not
  // the whole calendar) since this page has no single owning transaction.
  // Cache keyed by transactionId so switching days/months doesn't re-fetch
  // ones already loaded; failures just leave that transaction's entries on
  // the existing raw-field fallback.
  const [srsByTransaction, setSrsByTransaction] = useState({});
  useEffect(() => {
    const neededTxnIds = [...new Set(
      events
        .filter(e => (e.category === 'service' || !!e.providerType) && e.transactionId)
        .map(e => e.transactionId)
    )].filter(txnId => !(txnId in srsByTransaction));
    if (neededTxnIds.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      const token = await getFirebaseToken().catch(() => null);
      if (!token || cancelled) return;
      const results = await Promise.all(neededTxnIds.map(async (txnId) => {
        try {
          const res = await apiClient.get(API_ENDPOINTS.sentServiceRequests(txnId), {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 60000,
          });
          const arr = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.content || []);
          return [txnId, arr];
        } catch {
          return [txnId, []];
        }
      }));
      if (!cancelled) {
        setSrsByTransaction(prev => {
          const next = { ...prev };
          results.forEach(([txnId, arr]) => { next[txnId] = arr; });
          return next;
        });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, srsByTransaction]);

  // Resolves the linked SR for a service-type event: tier-1 by serviceRequestId,
  // tier-3 by eventType match within that SAME transaction's SR list (already
  // scoped by transactionId here, so — unlike the Timeline tab's cross-
  // transaction srByServiceType — this can't cross-match a different
  // transaction's SR of the same service type).
  const resolveLinkedSr = (ev) => {
    const isServiceEvent = ev.category === 'service' || !!ev.providerType;
    if (!isServiceEvent || !ev.transactionId) return null;
    const txnSrs = srsByTransaction[ev.transactionId];
    if (!txnSrs) return null;
    if (ev.serviceRequestId) {
      const byId = txnSrs.find(sr => (sr.id || sr.requestId || sr.serviceRequestId) === ev.serviceRequestId);
      if (byId) return byId;
    }
    const evType = (ev.eventType || '').toLowerCase();
    return txnSrs.find(sr => (sr.serviceType || '').toLowerCase() === evType) || null;
  };

  // Load transactions for the optional dropdown. Broker/Brokerage users aren't
  // agent participants on their agents' deals, so `viewingRole: 'AGENT'`
  // returned nothing for them — they use the same scoping as TransactionsPage.js
  // (Brokerage: brokerage fan-out endpoint; Broker: linked agents' agentIds).
  // Every other role keeps the original request unchanged.
  useEffect(() => {
    (async () => {
      setTxnLoading(true);
      try {
        const token = await getFirebaseToken();
        const role = getActiveRoleFromStorage();
        const isBroker = CALENDAR_BROKER_ROLES.includes(role);
        const isBrokerage = role === 'BROKERAGE';
        const headers = { Authorization: `Bearer ${token}` };

        const url = API_ENDPOINTS.transactions;
        let params = { status: ['PENDING', 'ACTIVE'], viewingRole: 'AGENT' };

        if (isBrokerage) {
          // brokerage/transactions binds a SINGLE `status` (repeated values
          // collapse to the first), so fetch each status and merge.
          const results = await Promise.allSettled(
            BROKER_TXN_STATUSES.map(status =>
              apiClient.get(API_ENDPOINTS.brokerageTransactions, {
                headers,
                params: { status, viewingRole: 'BROKERAGE', size: 100 },
              })
            )
          );
          const seen = new Set();
          const merged = [];
          results.forEach(r => {
            if (r.status !== 'fulfilled') {
              console.warn('[Calendar] Brokerage transactions fetch failed:', r.reason?.message || r.reason);
              return;
            }
            const d = r.value.data;
            const rows = d?.content || d?.data?.content || (Array.isArray(d?.data) ? d.data : null) || (Array.isArray(d) ? d : []);
            rows.forEach(t => {
              const key = String(t.id);
              if (!seen.has(key)) { seen.add(key); merged.push(t); }
            });
          });
          setTransactions(merged);
          return;
        } else if (isBroker) {
          // Same agent-ID source as TransactionsPage.js: staff roles resolve
          // their supervising broker's agents; a BROKER uses broker/agents.
          let agentIds = [];
          if (role === 'BROKER_MANAGER' || role === 'BROKER_ADMIN') {
            const idsRes = await apiClient.get(
              `${API_ENDPOINTS.brokerTransactionOwnerIds}?activeRole=${encodeURIComponent(role)}`,
              { headers },
            );
            agentIds = Array.isArray(idsRes.data) ? idsRes.data : [];
          } else {
            const agentsRes = await apiClient.get(API_ENDPOINTS.brokerAgents, { headers });
            const agents = Array.isArray(agentsRes.data)
              ? agentsRes.data
              : (agentsRes.data?.data || agentsRes.data?.content || []);
            agentIds = agents.map(a => a.userId).filter(Boolean);
          }
          // Same as TransactionsPage.js: with no linked agents, omit agentIds so
          // the backend returns every transaction the broker can access directly.
          params = {
            status: BROKER_TXN_STATUSES,
            ...(agentIds.length > 0 && { agentIds }),
            size: 100,
          };
        }

        const res = await apiClient.get(url, {
          headers,
          params,
          paramsSerializer: (params) => {
            const sp = new URLSearchParams();
            for (const [key, val] of Object.entries(params)) {
              if (Array.isArray(val)) val.forEach(v => sp.append(key, v));
              else if (val != null) sp.append(key, String(val));
            }
            return sp.toString();
          },
        });
        const d = res.data;
        const list =
          d?.content ||
          d?.transactions ||
          d?.transactionCards ||
          d?.data?.content ||
          (Array.isArray(d?.data) ? d.data : null) ||
          (Array.isArray(d) ? d : []);
        setTransactions(Array.isArray(list) ? list : []);
      } catch (err) {
        // dropdown is optional — don't block the form, but leave a trace
        console.warn('[Calendar] Failed to load transactions for dropdown:', err?.message || err);
      } finally {
        setTxnLoading(false);
      }
    })();
  }, []);

  // Fetch all calendar events on load (and after a save)
  const fetchAllEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const token = await getFirebaseToken();
      const res   = await apiClient.get(
        `${API_BASE_URL}/api/v1/calendar/events`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const arr = Array.isArray(res.data?.data) ? res.data.data : [];
      setAllEvents(arr);
      // Build the full dot-map from all events at once
      const map = {};
      arr.forEach(e => {
        // Key the dot by the viewer-local day (same basis as the selected-day event
        // list at line ~387 and formatEventForViewer's label) — NOT the raw UTC
        // scheduledDate. A UTC-encoded event can fall on a different local day, which
        // put the dot one day off from where the event's row/label actually shows.
        const dayKey = eventLocalDateKey(e, viewerTimezone) || e.scheduledDate;
        if (dayKey) {
          if (!map[dayKey]) map[dayKey] = [];
          map[dayKey].push(EVENT_COLORS[e.eventType] || '#8b5cf6');
        }
      });
      setDotMap(map);
    } catch {
      setAllEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => { fetchAllEvents(); }, [fetchAllEvents]);

  // Fetch every dated timeline step from the dedicated calendar-timeline endpoint —
  // prospect stages + active milestones + listing stages across all the user's deals,
  // uncapped (no next-action limit, no window). Separate from the Dashboard/Notification
  // code paths. Events are fetched separately (calendar_events), so nothing duplicates.
  const fetchTimelineSteps = useCallback(async () => {
    setTimelineLoading(true);
    try {
      const token = await getFirebaseToken();
      const res   = await apiClient.get(
        `${API_BASE_URL}/api/v1/transactions/calendar-timeline`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const raw   = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      // Label Overdue/Due today/Upcoming against the viewer's own profile-state
      // timezone day, not the device day (same zone the grid/events already use).
      const cards = transformTimelineItems(raw, nowWallClockInViewerZone(viewerTimezone).localDate);
      setTimelineCards(cards);
      const map = {};
      cards.forEach(c => {
        if (c.dueDate) {
          if (!map[c.dueDate]) map[c.dueDate] = [];
          map[c.dueDate].push(EVENT_COLORS['Milestone']);
        }
      });
      setTimelineDotMap(map);
    } catch {
      setTimelineCards([]);
      setTimelineDotMap({});
    } finally {
      setTimelineLoading(false);
    }
  }, []);

  useEffect(() => { fetchTimelineSteps(); }, [fetchTimelineSteps]);

  // Fetch note reminders — read-only, displayed alongside events/timeline cards in
  // the day panel. Server-side hasReminder=true filter already excludes notes with
  // no reminderDate, so no client-side filtering is needed here.
  const fetchReminderNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const token = await getFirebaseToken();
      const notes = await notesService.listNotes(token, { hasReminder: true });
      const list  = Array.isArray(notes) ? notes : (notes?.data || notes?.content || []);
      setReminderNotes(list);
      const map = {};
      list.forEach(n => {
        if (n.reminderDate) {
          if (!map[n.reminderDate]) map[n.reminderDate] = [];
          map[n.reminderDate].push(NOTE_REMINDER_COLOR);
        }
      });
      setNotesDotMap(map);
    } catch {
      setReminderNotes([]);
      setNotesDotMap({});
    } finally {
      setNotesLoading(false);
    }
  }, []);

  useEffect(() => { fetchReminderNotes(); }, [fetchReminderNotes]);

  // ── Calendar navigation ──────────────────────────────────────────────────────
  const prevMonth = () => {
    if (curMonth === 0) { setCurYear(y => y - 1); setCurMonth(11); }
    else setCurMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (curMonth === 11) { setCurYear(y => y + 1); setCurMonth(0); }
    else setCurMonth(m => m + 1);
  };
  const goToday = () => {
    setCurYear(todayDate.getFullYear());
    setCurMonth(todayDate.getMonth());
    const d = new Date(todayDate);
    setSelDate(d);
    setForm(prev => ({ ...prev, scheduledDate: fmtDate(d) }));
  };

  const handleDayClick = (cell) => {
    if (cell.other) return;
    const d = new Date(curYear, curMonth, cell.d);
    d.setHours(0, 0, 0, 0);
    setSelDate(d);
    setForm(prev => ({ ...prev, scheduledDate: fmtDate(d) }));
  };

  // ── Modal ────────────────────────────────────────────────────────────────────
  const openModal = () => {
    setEditEventId(null);
    setViewOnly(false);
    setViewingSrEvent(false);
    setForm(emptyForm(selDate, nowInViewerZone.localTime, eventTypeOptions[0] || DEFAULT_EVENT_TYPE));
    setSaveError('');
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setSaveError(''); setEditEventId(null); setViewOnly(false); setViewingSrEvent(false); };

  // readOnly=true opens the same dialog in View Only mode (eye button on a
  // card) — fields disabled, no save; available to attendees, not just organizers.
  const openEditModal = async (ev, readOnly = false) => {
    setEditEventId(ev.eventId);
    setViewOnly(readOnly);
    setViewingSrEvent(ev.category === 'service' || !!ev.providerType);
    setForm(emptyForm(selDate));
    setSaveError('');
    setEditLoading(true);
    setShowModal(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(
        `${API_BASE_URL}/api/v1/calendar/events/${ev.eventId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data?.data || res.data || {};
      // UTC-marked events are stored as UTC wall-clock — the raw scheduledDate/
      // scheduledTime are 7-8h ahead of what the viewer originally typed in
      // the PDT/PST picker. Convert back to viewer-zone wall-clock so the
      // pickers show the same values the card renders. Legacy events without
      // a timeZone marker keep the raw display (naive local strings).
      let editDate = d.scheduledDate || fmtDate(selDate);
      let editTime = d.scheduledTime || '10:00';
      if (d.timeZone === 'UTC' && d.scheduledDate && d.scheduledTime) {
        const isoStr = `${d.scheduledDate}T${d.scheduledTime}:00Z`;
        const { localDate, localTime } = instantToWallClockInZone(isoStr, viewerTimezone);
        if (localDate) editDate = localDate;
        if (localTime) editTime = localTime;
      }
      // Reconstruct End from the stored duration. All day short-circuits
      // and the End picker is hidden until the user unticks it.
      const rawDuration = (d.duration || '1 hour').toString();
      const isAllDay = d.allDay === true || /^all\s*day$/i.test(rawDuration);
      let durationMinutes = 60;
      const m = rawDuration.match(/([0-9]+(?:\.[0-9]+)?)\s*(min|hour|hr|day)/i);
      if (m) {
        const amount = parseFloat(m[1]);
        const unit = m[2].toLowerCase();
        durationMinutes = unit.startsWith('min') ? Math.round(amount)
          : unit.startsWith('d') ? Math.round(amount * 24 * 60)
          : Math.round(amount * 60);
      }
      setForm({
        title:         d.title || '',
        // An existing event already has whatever title the user picked —
        // treat it as touched so Event-type / Transaction changes during
        // this edit session don't overwrite it. Same reasoning for Location.
        titleTouched:  true,
        locationTouched: true,
        scheduledDate: editDate,
        scheduledTime: editTime,
        endTime:       isAllDay ? '' : addMinutesToTime(editTime, durationMinutes),
        allDay:        isAllDay,
        eventType:     d.eventType || DEFAULT_EVENT_TYPE,
        description:   d.description || '',
        location:      d.location || '',
        transactionId: d.transactionId || '',
        invitees: Array.isArray(d.attendees)
          ? d.attendees.filter(a => a.role !== 'ORGANIZER' && a.email).map(a => a.email).join(', ')
          : '',
        sendEmailInvites: true,
        existingInvitees: Array.isArray(d.attendees)
          ? d.attendees.filter(a => a.role !== 'ORGANIZER' && a.email).map(a => a.email)
          : [],
      });
    } catch {
      setSaveError('Failed to load event details. Please try again.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      // Typing into Title / Location turns off the respective auto-fill —
      // stop rewriting whatever the user chose when Event type / Transaction
      // changes afterwards.
      if (name === 'title') next.titleTouched = true;
      if (name === 'location') next.locationTouched = true;
      // Switching to a Reminder clears All day (per spec: Start only, no All day).
      if (name === 'eventType' && isReminderType(value)) next.allDay = false;
      return next;
    });
  };

  // Auto-fill Title with "[Event type] – [street address]" when a transaction
  // is tagged, or "[Event type]" when it isn't. Stops once the user has
  // typed into Title (titleTouched). Mirrors the mobile behavior.
  useEffect(() => {
    if (form.titleTouched) return;
    const typeLabel = form.eventType || '';
    const txn = transactions.find(x => String(x.id) === String(form.transactionId));
    const address = txn ? (txn.propertyAddress || '') : '';
    const auto = address ? `${typeLabel} – ${address}` : typeLabel;
    if (auto === form.title) return;
    setForm(prev => (prev.titleTouched ? prev : { ...prev, title: auto }));
  }, [form.eventType, form.transactionId, form.titleTouched, form.title, transactions]);

  // Auto-fill Location with the transaction's property address when a
  // transaction is tagged. Stops once the user has typed into Location
  // (locationTouched). Clearing the transaction wipes the seeded address so
  // Location doesn't carry stale data from the previous transaction forward.
  useEffect(() => {
    if (form.locationTouched) return;
    const txn = transactions.find(x => String(x.id) === String(form.transactionId));
    const address = txn ? (txn.propertyAddress || '') : '';
    if (address === form.location) return;
    setForm(prev => (prev.locationTouched ? prev : { ...prev, location: address }));
  }, [form.transactionId, form.locationTouched, form.location, transactions]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    try {
      // End-time guard. Skipped for Reminder (no End picker) and All day
      // (times are hidden). Everything else must have End > Start.
      if (!form.allDay && !isReminderType(form.eventType)
          && form.scheduledTime && form.endTime
          && minutesBetweenTimes(form.scheduledTime, form.endTime) <= 0) {
        setSaveError('End time must be after Start time.');
        return;
      }
      const token      = await getFirebaseToken();
      // With the e-mailed-invite feature on, guests are parsed and validated here
      // (addresses are never silently dropped). Off: the original behavior.
      const inviteParse = EMAIL_INVITES_ENABLED ? parseInvitees(form.invitees) : null;
      if (inviteParse && inviteParse.invalid.length) {
        setSaveError(`These don't look like email addresses: ${inviteParse.invalid.join(', ')}`);
        return;
      }
      const inviteesArr = inviteParse
        ? inviteParse.emails.map(email => ({ email }))
        : (form.invitees
          ? form.invitees.split(',').map(s => s.trim()).filter(Boolean).map(email => ({ email }))
          : []);
      const sendInvites = inviteParse
        ? shouldSendEmailInvites({
            enabled: true,
            checked: form.sendEmailInvites,
            transactionId: form.transactionId,
            emails: inviteParse.emails,
          })
        : false;
      // Save in UTC: convert the picker's local wall-clock values before
      // sending, and mark the event so any viewer renders it in their own
      // profile timezone.
      const { scheduledDate: utcDate, scheduledTime: utcTime } =
        toUtcWallClock(form.scheduledDate, form.scheduledTime || null);
      const payload = {
        title:         form.title,
        scheduledDate: utcDate,
        scheduledTime: utcTime,
        // Opts this page (and only this page) into having scheduledTime
        // actually persisted — see CreateEventRequest.scheduledTimeProvided
        // (rialty-calendar-service). Every other caller of this same POST/PUT
        // endpoint (SP Calendar's own Add Event, Transaction Timeline's Add
        // to Calendar, Create Service Request) omits this flag and keeps
        // scheduledTime silently dropped server-side, unchanged.
        scheduledTimeProvided: true,
        timeZone:      'UTC',
        // Derived from the Start/End pickers. Reminder has no End — spec
        // says Start only — so a nominal 15-minute window is persisted so
        // the ICS has a sane DTEND and the event doesn't bleed across the
        // calendar as a zero-length point.
        duration: form.allDay
          ? 'All day'
          : isReminderType(form.eventType)
            ? '15 minutes'
            : `${minutesBetweenTimes(form.scheduledTime, form.endTime)} minutes`,
        eventType:     form.eventType,
        // NOTE: deliberately NOT setting `category: 'service'` here — that
        // marker is what distinguishes SR-linked calendar events (written by
        // CreateServiceRequestPage.js / TransactionDetailPage.js) from
        // user-created "personal" events on this page. The eventType label
        // may coincide with a service-catalog label (e.g. "Appraisal") but
        // the entry itself is still user-created, so leave `category` unset
        // and let isServiceEvent gate rendering to real SR-linked events.
        allDay:        !!form.allDay,
        ...(form.description   && { description:   form.description }),
        ...(form.location      && { location:       form.location }),
        ...(form.transactionId && { transactionId:  form.transactionId }),
        ...(inviteesArr.length  && { invitees:       inviteesArr }),
        ...(sendInvites         && { sendEmailInvites: true }),
      };
      if (editEventId) {
        await apiClient.put(
          `${API_BASE_URL}/api/v1/calendar/events/${editEventId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // PUT ignores guests, so add any new ones through /invite (this is also
        // what e-mails them). Guests can't be removed from here yet.
        const added = inviteParse ? newInvitees(form.existingInvitees, inviteParse.emails) : [];
        if (added.length) {
          try {
            await apiClient.post(
              `${API_BASE_URL}/api/v1/calendar/events/${editEventId}/invite`,
              { invitees: added.map(email => ({ email })), ...(sendInvites && { sendEmailInvites: true }) },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (inviteErr) {
            setSaveError(`Event saved, but adding guests failed: ${inviteErr?.response?.data?.message || 'please try again.'}`);
            fetchAllEvents();
            return;
          }
        }
      } else {
        await apiClient.post(
          `${API_BASE_URL}/api/v1/calendar/events`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      closeModal();
      fetchAllEvents();
    } catch (err) {
      setSaveError(err?.response?.data?.message || 'Failed to save event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const token = await getFirebaseToken();
      await apiClient.delete(
        `${API_BASE_URL}/api/v1/calendar/events/${deleteConfirmId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeleteConfirmId(null);
      fetchAllEvents();
    } catch (err) {
      setDeleteError(err?.response?.data?.message || 'Failed to delete event. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Helpers for event badge ──────────────────────────────────────────────────
  const badgeCls = (status) => {
    const s = (status || '').toUpperCase();
    if (!status || s === 'CONFIRMED' || s === 'ACTIVE') return 'acal-badge-confirmed';
    if (s === 'MILESTONE') return 'acal-badge-milestone';
    return 'acal-badge-pending';
  };
  const badgeLbl = (status) => {
    if (!status) return 'Confirmed';
    const s = status.toUpperCase();
    if (s === 'CONFIRMED') return 'Confirmed';
    if (s === 'MILESTONE') return 'Milestone';
    // Use the actual API status value, title-cased (e.g. "ACTIVE" → "Active")
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Timeline card status badge — color-coded so status is the visually prominent
  // element on the card (separate from the type/phase/side pill row).
  const timelineStatusCls = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'COMPLETED' || s === 'DONE') return 'acal-tl-status-completed';
    if (s === 'IN_PROGRESS' || s === 'ACTIVE') return 'acal-tl-status-progress';
    if (s === 'SKIPPED' || s === 'WAIVED') return 'acal-tl-status-skipped';
    return 'acal-tl-status-pending'; // NOT_STARTED, PENDING
  };
  const timelineStatusLbl = (status) => {
    if (!status) return 'Pending';
    return status.charAt(0) + status.slice(1).toLowerCase().replace(/_/g, ' ');
  };

  // Resolve a transactionId to a display name from the loaded transactions list.
  // Returns '' when not found (e.g. a closed transaction not in the active list),
  // so the card's 📁 line only renders when we actually have a name.
  const resolveTxnName = (id) => {
    if (!id) return '';
    const t = transactions.find(x => String(x.id) === String(id));
    return t ? (t.transactionName || t.name || t.propertyAddress || t.dealName || '') : '';
  };

  const eventCount = events.length;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="acal-page">
      <div className="acal-layout">

        {/* ── Left: mini calendar ── */}
        <div className="acal-left">
          {/* Month header */}
          <div className="acal-cal-header">
            <span className="acal-month-title">{monthLabel(curYear, curMonth)}</span>
            <div className="acal-cal-nav">
              <button type="button" className="acal-today-btn" onClick={goToday}>Today</button>
              <button type="button" className="acal-nav-arrow" onClick={prevMonth}><ChevLeft /></button>
              <button type="button" className="acal-nav-arrow" onClick={nextMonth}><ChevRight /></button>
            </div>
          </div>

          {/* Weekday labels */}
          <div className="acal-weekdays">
            {WEEKDAYS.map(w => <div key={w} className="acal-weekday">{w}</div>)}
          </div>

          {/* Grid */}
          <div className="acal-grid">
            {grid.map((cell, i) => {
              const cellKey  = cell.other ? null : fmtDate(new Date(curYear, curMonth, cell.d));
              const isToday  = cellKey === todayKey;
              const isSel    = cellKey === selKey;
              const allDotsForCell = [...(dotMap[cellKey] || []), ...(timelineDotMap[cellKey] || []), ...(notesDotMap[cellKey] || [])];
              const dots         = allDotsForCell.slice(0, 3);
              const overflowCount = Math.max(0, allDotsForCell.length - 3);
              return (
                <div
                  key={i}
                  role={cell.other ? undefined : 'button'}
                  tabIndex={cell.other ? undefined : 0}
                  onClick={() => handleDayClick(cell)}
                  onKeyDown={(e) => !cell.other && e.key === 'Enter' && handleDayClick(cell)}
                  className={[
                    'acal-grid-cell',
                    cell.other ? '' : 'acal-cell-cur',
                    isSel      ? 'acal-cell-selected' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className={[
                    'acal-day-num',
                    isToday            ? 'acal-day-today'   : '',
                    isSel && !isToday  ? 'acal-day-sel-num' : '',
                    cell.other         ? 'acal-day-other'   : '',
                  ].filter(Boolean).join(' ')}>
                    {cell.d}
                  </div>
                  {dots.length > 0 && (
                    <div className="acal-dots">
                      {dots.map((c, di) => (
                        <div key={di} className="acal-dot" style={{ background: c }} />
                      ))}
                      {overflowCount > 0 && (
                        <span className="acal-dots-overflow">+{overflowCount}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="acal-legend">
            {LEGEND.map(({ label, color }) => (
              <div key={label} className="acal-legend-item">
                <div className="acal-legend-dot" style={{ background: color }} />
                <span>{label}</span>
              </div>
            ))}
          </div>

          {/* Add Event */}
          <button type="button" className="acal-add-btn" onClick={openModal}>
            <PlusIcon /> Add Event
          </button>
        </div>

        {/* ── Right: day events panel ── */}
        <div className="acal-right">
          <div className="acal-day-panel">
          <div className="acal-day-header">
            <div>
              <p className="acal-day-title">{dayLabel(selDate)}</p>
              <p className="acal-day-sub">
                {eventsLoading || timelineLoading || notesLoading
                  ? 'Loading…'
                  : (eventCount + dayTimelineCards.length + dayReminderNotes.length) === 0
                    ? 'Nothing scheduled'
                    : [
                        eventCount ? `${eventCount} event${eventCount > 1 ? 's' : ''}` : null,
                        dayTimelineCards.length ? `${dayTimelineCards.length} timeline item${dayTimelineCards.length > 1 ? 's' : ''}` : null,
                        dayReminderNotes.length ? `${dayReminderNotes.length} note reminder${dayReminderNotes.length > 1 ? 's' : ''}` : null,
                      ].filter(Boolean).join(', ')}
              </p>
            </div>
            {!eventsLoading && !timelineLoading && !notesLoading && (
              <span className="acal-count-badge">
                {eventCount + dayTimelineCards.length + dayReminderNotes.length} item{(eventCount + dayTimelineCards.length + dayReminderNotes.length) !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {eventsLoading || timelineLoading || notesLoading ? (
            <div className="acal-empty-state">
              <p>Loading events…</p>
            </div>
          ) : eventCount === 0 && dayTimelineCards.length === 0 && dayReminderNotes.length === 0 ? (
            <div className="acal-empty-state">
              <CalendarIcon />
              <p>No events on this day.</p>
              <span>Click <strong>+ Add Event</strong> to schedule one.</span>
            </div>
          ) : (
            <>
            {eventCount > 0 && (
              <div className="acal-day-section">
                <h4 className="acal-day-section-label">Events</h4>
                <div className="acal-events-list">
                  {events.map((ev, i) => {
                const color   = EVENT_COLORS[ev.eventType] || '#8b5cf6';
                // Prefer the linked SR's requestedTime (always a real UTC
                // instant) over the calendar event's own scheduledTime when
                // one can be resolved — same fallback-safe pattern as
                // TransactionTimeline.js. Falls back to today's behavior
                // (formatEventForViewer, then raw ev.time) when no SR is
                // linked or its data hasn't loaded yet.
                const linkedSr = resolveLinkedSr(ev);
                const timeStr = linkedSr?.requestedTime
                  ? formatInstantForViewer(linkedSr.requestedTime, viewerTimezone).timeLabel
                  : (formatEventForViewer(ev, viewerTimezone).timeLabel || ev.time || '');
                const txnName = ev.transaction || resolveTxnName(ev.transactionId);
                // Service-request-backed calendar events shouldn't be edited from
                // the calendar — the SR record is the source of truth for time /
                // provider / status, so mutating this side would drift them apart.
                const isServiceEvent = ev.category === 'service' || !!ev.providerType;
                return (
                  <div
                    key={ev.eventId || ev.id || i}
                    className="acal-event-card"
                  >
                    <div className="acal-event-bar" style={{ background: color }} />
                    <div className="acal-event-body">
                      {txnName && <p className="acal-event-txn">📁 {txnName}</p>}
                      <p className="acal-event-title">{ev.title}</p>
                      <span className="acal-event-type-pill" style={{ background: `${color}1A`, color }}>
                        <span className="acal-event-type-dot" style={{ background: color }} />
                        {ev.eventType}
                      </span>
                      {timeStr && (
                        <p className="acal-event-time"><ClockIcon /> {timeStr}</p>
                      )}
                      {(ev.location || ev.description) && (
                        <div className="acal-event-meta">
                          {ev.location    && <span>📍 {ev.location}</span>}
                          {ev.description && <span>📝 {ev.description}</span>}
                        </div>
                      )}
                    </div>
                    <div className="acal-event-right">
                      {false && (
                        <span className={`acal-event-badge ${badgeCls(ev.status)}`}>
                          {badgeLbl(ev.status)}
                        </span>
                      )}
                      <div className="acal-ev-actions">
                        {/* SR-linked events are read-only from the calendar (the
                            SR record is the source of truth) — show only the
                            eye so the viewer can inspect details.
                            Self-added events are editable — show the pencil
                            (which opens the same dialog in edit mode). */}
                        {isServiceEvent ? (
                          <button
                            type="button"
                            className="acal-ev-action-btn acal-ev-view-btn"
                            onClick={(e) => { e.stopPropagation(); openEditModal(ev, true); }}
                            title="View event details"
                          >
                            <EyeIcon />
                          </button>
                        ) : ev.organizer && (
                          <button
                            type="button"
                            className="acal-ev-action-btn acal-ev-edit-btn"
                            onClick={(e) => { e.stopPropagation(); openEditModal(ev); }}
                            title="Edit event"
                          >
                            <EditIcon />
                          </button>
                        )}
                        {ev.organizer && (
                          <button
                            type="button"
                            className="acal-ev-action-btn acal-ev-delete-btn"
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(ev.eventId); setDeleteError(''); }}
                            title="Delete event"
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
                </div>
              </div>
            )}

            {/* ── Timeline due — tasks from /api/v1/dashboard, read-only. ── */}
            {!timelineLoading && dayTimelineCards.length > 0 && (
              <div className="acal-day-section">
                <h4 className="acal-day-section-label">Timeline due</h4>
                <div className="acal-events-list">
                  {dayTimelineCards.map((card) => (
                    <div key={card.id} className="acal-timeline-card">
                      <div className="acal-timeline-bar" style={{ background: EVENT_COLORS['Milestone'] }} />
                      <div className="acal-timeline-body">
                        <p className="acal-timeline-txn">📁 {card.transactionName}</p>
                        <p className="acal-timeline-name">{card.nextActionName}</p>
                        <div className="acal-timeline-pill-row">
                          <span className="acal-timeline-pill acal-timeline-pill-type">{card.type}</span>
                          <span className="acal-timeline-pill acal-timeline-pill-phase">{card.phase}</span>
                        </div>
                        {card.dueLabel && <p className="acal-timeline-due">{card.dueLabel}</p>}
                      </div>
                      <div className="acal-timeline-right">
                        <span className={`acal-tl-status-badge ${timelineStatusCls(card.status)}`}>
                          {timelineStatusLbl(card.status)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Note reminders — from My Notes (hasReminder=true), read-only. ── */}
            {!notesLoading && dayReminderNotes.length > 0 && (
              <div className="acal-day-section">
                <h4 className="acal-day-section-label">Note reminders</h4>
                <div className="acal-events-list">
                  {dayReminderNotes.map((note) => (
                    <div key={note.id} className="acal-note-card">
                      <div className="acal-note-bar" style={{ background: NOTE_REMINDER_COLOR }} />
                      <div className="acal-note-body">
                        <span className="acal-note-pill" style={{ background: `${NOTE_REMINDER_COLOR}1A`, color: NOTE_REMINDER_COLOR }}>
                          Note Reminder
                        </span>
                        <p className="acal-note-text">{note.text}</p>
                        {note.linkLabel && <p className="acal-note-link">🔗 {note.linkLabel}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </>
          )}
          </div>
        </div>
      </div>

      {/* ── Add Event modal ── */}
      {showModal && (
        <div
          className="acal-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && closeModal()}
        >
          <div className="acal-modal">
            <div className="acal-modal-header">
              <p className="acal-modal-title">
                {viewOnly ? 'View Event Information' : editEventId ? 'Edit Event' : 'Add Event'}
              </p>
              <button type="button" className="acal-modal-close" onClick={closeModal}>
                <XIcon />
              </button>
            </div>

            <form onSubmit={handleSave} style={{ position: 'relative' }}>
              {editLoading && (
                <div className="acal-form-loading">
                  <div className="acal-form-spinner" />
                </div>
              )}
              {/* View Only: every field disabled via fieldset; custom dropdown
                  additionally gets pointer-events:none (it isn't a native input) */}
              <fieldset
                disabled={viewOnly}
                style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}
              >
              {/* Event type (first, mirrors the new Add Event layout). The
                  colored dot next to the <select> shows the current catalog
                  item's group colour — native <option> can't carry styling,
                  so the indicator sits on the wrapper instead. */}
              <div className="acal-form-group">
                <label className="acal-form-label">
                  Event type <span className="acal-req">*</span>
                </label>
                <div className="acal-select-with-dot">
                  {EVENT_COLORS[form.eventType] && (
                    <span className="acal-select-dot" style={{ background: EVENT_COLORS[form.eventType] }} />
                  )}
                <select
                  className="acal-form-select acal-form-select--with-dot"
                  name="eventType"
                  value={form.eventType}
                  onChange={handleFormChange}
                  required
                >
                  {/* Prepend an off-catalog current value so the select never silently
                      snaps to the first option on edit (same comment as before). */}
                  {form.eventType && !eventTypeOptions.includes(form.eventType) && (
                    <option key={form.eventType} value={form.eventType}>{form.eventType}</option>
                  )}
                  {EVENT_TYPE_GROUPS.map(g => (
                    <optgroup key={g.label} label={g.label}>
                      {g.items.map(t => <option key={t} value={t}>{t}</option>)}
                    </optgroup>
                  ))}
                </select>
                </div>
                {!viewOnly && isServicesType(form.eventType) && (
                  <p className="acal-raise-sr-hint">
                    Need a provider?{' '}
                    <button
                      type="button"
                      className="acal-raise-sr-link"
                      onClick={() => {
                        // Open the SR flow with Event type / Date / Transaction
                        // prefilled. Shape matches CreateServiceRequestPage's
                        // location.state contract (sibling to fromChecklist).
                        const fromCalendar = {
                          eventType: form.eventType,
                          scheduledDate: form.scheduledDate,
                          transactionId: form.transactionId,
                        };
                        closeModal();
                        navigate('/create-service-request', { state: { fromCalendar } });
                      }}
                    >
                      Raise service request
                    </button>
                  </p>
                )}
              </div>

              {/* Transaction (optional) */}
              <div className="acal-form-group acal-form-group--txn">
                <label className="acal-form-label">
                  Transaction <span className="acal-opt">optional</span>
                </label>
                <div style={viewOnly ? { pointerEvents: 'none', opacity: 0.75 } : undefined}>
                  <TransactionDropdown
                    transactions={transactions}
                    value={form.transactionId}
                    onChange={(id) => setForm(prev => ({ ...prev, transactionId: id }))}
                    placeholder="— Select transaction —"
                    isLoading={txnLoading}
                  />
                </div>
              </div>

              {/* Title */}
              <div className="acal-form-group">
                <label className="acal-form-label">
                  Title <span className="acal-req">*</span>
                </label>
                <input
                  className="acal-form-input"
                  type="text"
                  name="title"
                  placeholder="e.g. Home Inspection"
                  value={form.title}
                  onChange={handleFormChange}
                  required
                />
              </div>

              {/* Date / Start / End — End is hidden for Reminder (Start only),
                  All day, and SR-backed events. Start is hidden for All day
                  and SR-backed events. */}
              <div className={`acal-form-row${viewingSrEvent || form.allDay || isReminderType(form.eventType) ? ' acal-form-row--single' : ''}`}>
                <div className="acal-form-group">
                  <label className="acal-form-label">
                    Date <span className="acal-req">*</span>
                  </label>
                  <input
                    className="acal-form-input"
                    type="date"
                    name="scheduledDate"
                    value={form.scheduledDate}
                    onChange={handleFormChange}
                    min={nowWallClockInViewerZone(viewerTimezone).localDate}
                    required
                  />
                </div>
                {!viewingSrEvent && !form.allDay && (
                  <div className="acal-form-group">
                    <label className="acal-form-label">
                      Start <span className="acal-req">*</span>
                    </label>
                    <input
                      className="acal-form-input"
                      type="time"
                      name="scheduledTime"
                      value={form.scheduledTime}
                      onChange={(e) => {
                        // Nudge End forward by the previous Start/End span so
                        // a 2 h window stays 2 h after a Start change, and End
                        // never ends up before Start.
                        const nextStart = e.target.value;
                        setForm(prev => {
                          const prevSpan = minutesBetweenTimes(prev.scheduledTime, prev.endTime);
                          const needsBump = !prev.endTime
                            || minutesBetweenTimes(nextStart, prev.endTime) <= 0;
                          return {
                            ...prev,
                            scheduledTime: nextStart,
                            endTime: needsBump ? addMinutesToTime(nextStart, prevSpan || 60) : prev.endTime,
                          };
                        });
                      }}
                      required
                    />
                  </div>
                )}
                {!viewingSrEvent && !form.allDay && !isReminderType(form.eventType) && (
                  <div className="acal-form-group">
                    <label className="acal-form-label">
                      End <span className="acal-req">*</span>
                    </label>
                    <input
                      className="acal-form-input"
                      type="time"
                      name="endTime"
                      value={form.endTime}
                      onChange={handleFormChange}
                      min={form.scheduledTime || undefined}
                      required
                    />
                  </div>
                )}
              </div>

              {/* All day — hidden for Reminder (per spec: Start only) and
                  SR-backed events (SR record owns scheduling). */}
              {!viewingSrEvent && !isReminderType(form.eventType) && (
                <div className="acal-form-group acal-form-group--allday">
                  <label className="acal-allday-check">
                    <input
                      type="checkbox"
                      checked={!!form.allDay}
                      onChange={(e) => setForm(prev => ({ ...prev, allDay: e.target.checked }))}
                    />
                    All day
                  </label>
                </div>
              )}

              {/* Guests (optional) — hidden unless the e-mailed-invite feature is
                  on, AND hidden entirely for Reminder / Blocked Time per spec. */}
              <div className="acal-form-group" style={EMAIL_INVITES_ENABLED && showGuestsForType(form.eventType) ? undefined : { display: 'none' }}>
                <label className="acal-form-label">
                  Guests <span className="acal-opt">optional</span>
                </label>
                {EMAIL_INVITES_ENABLED ? (
                  <InviteeInput
                    value={form.invitees}
                    onChange={(invitees) => setForm(prev => ({ ...prev, invitees }))}
                    lockedEmails={form.existingInvitees}
                  />
                ) : (
                  <input
                    className="acal-form-input"
                    type="text"
                    name="invitees"
                    placeholder="Add email addresses, separated by comma"
                    value={form.invitees}
                    onChange={handleFormChange}
                  />
                )}
                {EMAIL_INVITES_ENABLED && (() => {
                  const emails = parseInvitees(form.invitees).emails;
                  if (!emails.length) return null;
                  if (!canEmailInvites({ transactionId: form.transactionId, emails })) {
                    return (
                      <p className="acal-invite-hint">
                        Calendar invites aren't emailed for events linked to a transaction.
                      </p>
                    );
                  }
                  return (
                    <>
                      <label className="acal-invite-check">
                        <input
                          type="checkbox"
                          checked={!!form.sendEmailInvites}
                          onChange={(e) => setForm(prev => ({ ...prev, sendEmailInvites: e.target.checked }))}
                        />
                        Email a calendar invite to these guests
                      </label>
                      {editEventId && (
                        <p className="acal-invite-hint">
                          New guests are added when you save. Removing a guest isn't supported yet.
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Location (optional) — hidden for Reminder and Blocked Time. */}
              {showLocationForType(form.eventType) && (
                <div className="acal-form-group">
                  <label className="acal-form-label">
                    Location <span className="acal-opt">optional</span>
                  </label>
                  <input
                    className="acal-form-input"
                    type="text"
                    name="location"
                    placeholder="e.g. 123 Main St, San Francisco"
                    value={form.location}
                    onChange={handleFormChange}
                  />
                </div>
              )}

              {/* Description (optional) */}
              <div className="acal-form-group">
                <label className="acal-form-label">
                  Description <span className="acal-opt">optional</span>
                </label>
                <textarea
                  className="acal-form-textarea"
                  name="description"
                  placeholder="Add notes or focus areas…"
                  value={form.description}
                  onChange={handleFormChange}
                />
              </div>

              </fieldset>

              {saveError && <p className="acal-save-error">{saveError}</p>}

              {/* View Only: no footer buttons at all — dialog closes via ✕ */}
              {!viewOnly && (
                <div className="acal-modal-footer">
                  <button type="button" className="acal-btn-cancel" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="acal-btn-save" disabled={saving || editLoading}>
                    {saving
                      ? (editEventId ? 'Updating…' : 'Saving…')
                      : (editEventId ? 'Update event' : 'Save event')}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* ── Delete confirmation modal ── */}
      {deleteConfirmId && (
        <div
          className="acal-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !deleting && setDeleteConfirmId(null)}
        >
          <div className="acal-modal acal-modal--confirm">
            <div className="acal-modal-header">
              <p className="acal-modal-title">Delete Event</p>
              <button
                type="button"
                className="acal-modal-close"
                onClick={() => !deleting && setDeleteConfirmId(null)}
              >
                <XIcon />
              </button>
            </div>
            <div className="acal-confirm-body">
              <p>Are you sure you want to delete this event? This action cannot be undone.</p>
            </div>
            {deleteError && <p className="acal-save-error">{deleteError}</p>}
            <div className="acal-modal-footer">
              <button
                type="button"
                className="acal-btn-cancel"
                onClick={() => setDeleteConfirmId(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="acal-btn-delete"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentCalendarPage;
